import { useEffect, useMemo, useState } from 'react';

import type { IProspect } from '../../../shared/interfaces/prospects';
import type {
  ICommercialAddonsResponse,
  IQuote,
  IQuickCensusAgeSummaryEntry,
  IQuoteCensusFileState
} from '../../../shared/interfaces/quotes';
import ProspectsService from '../../../shared/services/api/prospects/prospects';
import {
  addonIsAgeDependent,
  buildProductGroups,
  extractEditQuoteState,
  initSelectionState,
  type IProductGroup,
  type SelectionState
} from '../../../shared/utils/quotes/quotes';

export interface IUseCreateQuoteDataParams {
  brokerId: string | null;
  userLoading: boolean;
  loadingAddons: boolean;
  loadingPlans: boolean;
  commercialAddons: ICommercialAddonsResponse;
  prospectId: number;
  isEditing: boolean;
  editingQuote: IQuote | null;
}

export interface IUseCreateQuoteDataResult {
  prospect: IProspect | null;
  loading: boolean;
  groups: IProductGroup[];
  employees: string;
  setEmployees: (value: string) => void;
  averageAge: string;
  setAverageAge: (value: string) => void;
  quoteName: string;
  setQuoteName: (value: string) => void;
  segment: string;
  setSegment: (value: string) => void;
  selection: SelectionState;
  setSelection: React.Dispatch<React.SetStateAction<SelectionState>>;
  censusSummaries: Record<string, IQuickCensusAgeSummaryEntry[]>;
  setCensusSummaries: React.Dispatch<React.SetStateAction<Record<string, IQuickCensusAgeSummaryEntry[]>>>;
  censusFile: IQuoteCensusFileState | null;
  setCensusFile: React.Dispatch<React.SetStateAction<IQuoteCensusFileState | null>>;
  initialPriceFactorsStr: string | null;
}

export const useCreateQuoteData = ({
  brokerId,
  userLoading,
  loadingAddons,
  loadingPlans,
  commercialAddons,
  prospectId,
  isEditing,
  editingQuote
}: IUseCreateQuoteDataParams): IUseCreateQuoteDataResult => {
  const [prospect, setProspect] = useState<IProspect | null>(null);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState('');
  const [averageAge, setAverageAge] = useState('');
  const [quoteName, setQuoteName] = useState('');
  const [segment, setSegment] = useState('');
  const [selection, setSelection] = useState<SelectionState>({});
  const [censusSummaries, setCensusSummaries] = useState<Record<string, IQuickCensusAgeSummaryEntry[]>>({});
  const [censusFile, setCensusFile] = useState<IQuoteCensusFileState | null>(null);
  const [initialPriceFactorsStr, setInitialPriceFactorsStr] = useState<string | null>(null);

  const groups = useMemo(() => buildProductGroups(commercialAddons), [commercialAddons]);

  useEffect(() => {
    const fetchData = async () => {
      if (userLoading || loadingAddons || loadingPlans) return;
      if (!brokerId || Number.isNaN(prospectId)) {
        setLoading(false);
        return;
      }
      try {
        setAverageAge('');
        setLoading(true);
        const prospects = await ProspectsService.getProspects(brokerId, { prospectId });
        const matched = prospects.find((item: IProspect) => item.id === prospectId) ?? null;
        const productGroups = buildProductGroups(commercialAddons);
        setProspect(matched);

        if (isEditing && editingQuote) {
          const editState = extractEditQuoteState(editingQuote, productGroups, matched?.workforceSize);

          setQuoteName(editState.quoteName);
          setSegment(editState.segment);
          setSelection(editState.selection);
          setCensusFile(editState.censusFile);
          setCensusSummaries(editState.censusSummaries);
          setEmployees(editState.employeesStr);
          setAverageAge(editState.averageAgeStr);
          setInitialPriceFactorsStr(editState.initialPriceFactorsStr);
        } else {
          setSelection(initSelectionState(productGroups));
          setEmployees(matched?.workforceSize == null ? '' : String(matched.workforceSize));
          setAverageAge('');
        }
      } catch {
        setProspect(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [brokerId, userLoading, loadingAddons, loadingPlans, commercialAddons, prospectId, isEditing, editingQuote]);

  useEffect(() => {
    const anyAgeDependentSelected = groups.some(
      (group) =>
        selection[group.key]?.selected &&
        group.employerPaid &&
        addonIsAgeDependent(group.addons.find((a) => a.id === selection[group.key]?.addonId))
    );
    if (!anyAgeDependentSelected) {
      setAverageAge('');
    }
  }, [groups, selection]);

  return {
    prospect,
    loading,
    groups,
    employees,
    setEmployees,
    averageAge,
    setAverageAge,
    quoteName,
    setQuoteName,
    segment,
    setSegment,
    selection,
    setSelection,
    censusSummaries,
    setCensusSummaries,
    censusFile,
    setCensusFile,
    initialPriceFactorsStr
  };
};
